import React, { useState, useContext } from 'react';
//@ts-ignore
import * as styles from './search-results.module.css';
import {
    InstantSearch,
    RefinementList,
    InfiniteHits,
    Configure,
    connectStateResults,
} from 'react-instantsearch-dom';
import Faq from '../Faq';
import { Faq as FaqType } from '../Global/DataUtils/faqQueries';
import VideoPlayer from '../Global/VideoPlayer';
import { KontentMedia } from '../../../types/KontentProps';
import {
    SupportDocument,
    SupportVideo,
} from '../ProductSupportLayout/productSupportTypes';
import { SupportSearchResultsProps } from '.';
import RichText from '../Global/RichText';
import CtaLink from '../Global/CtaLink';
import DebouncedSearchBox from './DebouncedSearchBox';
import { searchClient } from '../Global/DataUtils/algolia';
import {
    optimizeKontentImageUrl,
    imagePreset,
    buildKontentSrcSet,
} from '../Global/DataUtils';
import { PageMetadataContext } from '../../templates/PageMetaDataContext';
import { getLangPrefix } from '../../global/countries';
import {
    getAlgoliaIndexPrefix,
    getAlgoliaLangSuffix,
} from '../../global/utility';

interface Props extends SupportSearchResultsProps {
    term?: string;
    preferredLanguage?: String;
}

interface Hit {
    type: 'Manual' | 'FAQ' | 'Videos' | 'Support Page';
    slug: string;
    display_page_name: string;
    page_search_summary: string;
    page_search_image: KontentMedia['value'][];
    product_name: string;
    product_family: string;
    manuals: SupportDocument['elements'];
    faqs: FaqType['elements'];
    videos: SupportVideo['elements'];
}

const Manual: React.FC<Hit> = ({ manuals, product_name }) => {
    const manualImg = manuals?.image?.value[0]?.elements?.file?.value[0]?.url;

    return (
        <div className={`${styles.resultBox} ${styles.manuals}`}>
            {manualImg && (
                <div className={styles.resultImageWrapper}>
                    <img
                        src={optimizeKontentImageUrl(manualImg, {
                            ...imagePreset.card.desktop,
                            q: 95,
                        })}
                        srcSet={buildKontentSrcSet(
                            manualImg,
                            imagePreset.card.desktop.w,
                            {
                                fm: 'webp' as const,
                                q: 95,
                            }
                        )}
                        sizes="(max-width: 768px) 100vw, 33vw"
                        alt={product_name || 'Manual'}
                    />
                </div>
            )}
            <div
                className={`${styles.resultContent} ${
                    manualImg ? styles.hasImg : ''
                }`}
            >
                <h5>{manuals?.file?.value[0]?.description || product_name}</h5>
                <a href={manuals?.file?.value[0]?.url}>
                    {manuals?.document_type?.value[0]?.name}
                </a>
            </div>
        </div>
    );
};

const SupportPage: React.FC<Hit> = ({
    display_page_name,
    page_search_image,
    page_search_summary,
    slug,
    ...props
}) => {
    const preferredLanguage = useContext(PageMetadataContext).preferredLanguage;
    const langPrefix = getLangPrefix(preferredLanguage);
    const pageSearchImg = page_search_image[0]?.elements?.file?.value[0]?.url;
    const anchorURL = props.url != undefined ? langPrefix + props.url : slug;
    return (
        <div className={styles.resultBox}>
            {pageSearchImg && (
                <div className={styles.resultImageWrapper}>
                    <img
                        src={optimizeKontentImageUrl(pageSearchImg, {
                            ...imagePreset.card.desktop,
                            q: 95,
                        })}
                        srcSet={buildKontentSrcSet(
                            pageSearchImg,
                            imagePreset.card.desktop.w,
                            {
                                fm: 'webp' as const,
                                q: 95,
                            }
                        )}
                        sizes="(max-width: 768px) 100vw, 33vw"
                        alt={display_page_name || ''}
                    />
                </div>
            )}
            <div
                className={`${styles.resultContent} ${
                    pageSearchImg ? styles.hasImg : ''
                }`}
            >
                <a className={styles.pageName} href={anchorURL}>
                    {display_page_name}
                </a>
                <p>{page_search_summary}</p>
            </div>
        </div>
    );
};

const Video: React.FC<Hit> = ({ videos }) => {
    return (
        <div className={styles.videos}>
            <div>
                <div key={videos?.youtube_url?.value}>
                    <VideoPlayer src={videos?.youtube_url?.value} />
                    <p data-kontent-element-codename="caption">
                        {videos?.caption?.value}
                    </p>
                </div>
            </div>
        </div>
    );
};

const Hit = ({ hit }: { hit: Hit }, ...props) => {
    if (hit.type === 'Manual') {
        return (
            <div className={styles.hitWrapper}>
                <Manual {...hit} />
            </div>
        );
    }

    if (hit.type === 'Support Page') {
        return (
            <div className={styles.hitWrapper}>
                <SupportPage {...hit} props />
            </div>
        );
    }

    if (hit.type === 'FAQ') {
        return (
            <div className={styles.hitWrapper}>
                <Faq
                    faq={{
                        elements: hit.faqs,
                        preferred_language: 'default',
                    }}
                    truncateBody={true}
                />
            </div>
        );
    }

    if (hit.type === 'Videos') {
        return (
            <div className={styles.hitWrapper}>
                <Video {...hit} />
            </div>
        );
    }
};

const SupportSearchResults: React.FC<Props> = ({
    term = '',
    filter_cta_label,
    no_results_found_messaging,
    preferredLanguage,
    content_type_filter_label,
    product_type_filter_label,
    topic_type_filter_label,
    kontentItemId,
}) => {
    let searchTerm = term;
    const algoliaIndexPrefix = getAlgoliaIndexPrefix(
        process.env.GATSBY_ALGOLIA_ENV
    );
    // Algolia index suffixes are case-sensitive (e.g. `_GB-en`, `_AU-en`)
    // while preferredLanguage may be lowercased — denormalize before use.
    const [indexName, setIndexName] = useState(
        `${algoliaIndexPrefix}ProductSupportPages_${getAlgoliaLangSuffix(
            preferredLanguage as string | undefined
        )}`
    );
    const [showType, setShowType] = useState(true);
    const [showFamily, setShowFamily] = useState(true);
    const [showProduct, setShowProduct] = useState(true);

    //@ts-ignore
    const ResultWrapper = connectStateResults(({ searchResults, children }) =>
        searchResults && searchResults.nbHits !== 0 ? (
            children
        ) : (
            <div>
                {no_results_found_messaging &&
                    RichText(
                        no_results_found_messaging,
                        false,
                        undefined,
                        undefined,
                        'no_results_found_messaging'
                    )}
            </div>
        )
    );

    return (
        <section
            className={styles.searchResultsSection}
            data-kontent-item-id={kontentItemId}
        >
            <InstantSearch
                indexName={indexName}
                searchClient={searchClient}
                refresh={false}
            >
                <Configure hitsPerPage={20} />
                <div className={styles.searchBox}>
                    <DebouncedSearchBox
                        defaultRefinement={searchTerm}
                        delay={300}
                    />
                </div>
                <div className={styles.searchResultContainer}>
                    <div className={styles.refinementList}>
                        <div className={styles.filterContainer}>
                            {filter_cta_label?.value && (
                                <p
                                    className={styles.showing}
                                    data-kontent-element-codename="filter_cta_label"
                                >
                                    {filter_cta_label?.value}
                                </p>
                            )}
                            <button
                                className={styles.filterLabel}
                                onClick={() => setShowType(!showType)}
                            >
                                {content_type_filter_label?.value ||
                                    'Content Type'}
                            </button>
                            {showType && (
                                <RefinementList
                                    attribute="type"
                                    className={styles.refinementListItem}
                                />
                            )}
                            <button
                                className={styles.filterLabel}
                                onClick={() => setShowFamily(!showFamily)}
                            >
                                {product_type_filter_label?.value ||
                                    'Product Family'}
                            </button>
                            {showFamily && (
                                <RefinementList
                                    attribute="product_family"
                                    className={styles.refinementListItem}
                                />
                            )}
                            <button
                                className={styles.filterLabel}
                                onClick={() => setShowProduct(!showProduct)}
                            >
                                {topic_type_filter_label?.value || 'Topics'}
                            </button>
                            {showProduct && (
                                <RefinementList
                                    attribute="topic"
                                    limit={40}
                                    className={styles.refinementListItem}
                                />
                            )}
                        </div>
                    </div>
                    <div className={styles.searchResults}>
                        <div className={styles.hitList}>
                            <ResultWrapper>
                                <InfiniteHits
                                    hitComponent={Hit}
                                    translations={{ loadMore: 'Show More' }}
                                />
                            </ResultWrapper>
                        </div>
                    </div>
                </div>
            </InstantSearch>
        </section>
    );
};

export default SupportSearchResults;
